import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Breadcrumbs,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { get } from "src/utils/networkUtils";
import EditPlayerModal from "src/components/players/EditPlayerModal";

const PlayerDetailPage = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchPlayer = useCallback(() => {
    setLoading(true);
    setError(null);

    get(`/admin/players/${playerId}`)
      .then((response) => {
        const playerData = response?.data ?? response;
        setPlayer(playerData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch player:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to fetch player"
        );
        setLoading(false);
      });
  }, [playerId]);

  useEffect(() => {
    fetchPlayer();
  }, [fetchPlayer]);

  const handleBack = () => {
    navigate("/dashboard/players");
  };

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
  };

  const handlePlayerUpdated = () => {
    setSnackbar({
      open: true,
      message: "Player updated successfully",
      severity: "success",
    });
    fetchPlayer();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatPrice = (price) => {
    return price ? `£${parseFloat(price).toFixed(1)}m` : "£0.0m";
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Button
            onClick={() => navigate("/dashboard/players")}
            sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
          >
            Players
          </Button>
          <Typography color="text.primary">Error</Typography>
        </Breadcrumbs>
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!player) {
    return (
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Button
            onClick={() => navigate("/dashboard/players")}
            sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
          >
            Players
          </Button>
          <Typography color="text.primary">Not Found</Typography>
        </Breadcrumbs>
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Player not found</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Breadcrumbs sx={{ mb: 2 }}>
        <Button
          onClick={() => navigate("/dashboard/players")}
          sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
        >
          Players
        </Button>
        <Typography color="text.primary">
          {`${player.firstName || ""} ${player.lastName || ""}`.trim()}
        </Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">
          {`${player.firstName || ""} ${player.lastName || ""}`.trim()}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
          >
            Edit Player
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  fontSize: 40,
                }}
              >
                {player.firstName?.[0]}
                {player.lastName?.[0]}
              </Avatar>
            </Box>
          </Grid>

          <Grid item xs={12} md={10}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <Chip
                    label={player.position}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={player.status || "available"}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={
                      player.contestantShortName ||
                      player.contestantName ||
                      "Unknown Team"
                    }
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={player.hasLeft ? "Left League" : "Active"}
                    color={player.hasLeft ? "warning" : "success"}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h3">
                  {formatPrice(player.price)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Points
                </Typography>
                <Typography variant="h6">{player.totalPoints || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Average Points
                </Typography>
                <Typography variant="h6">
                  {player.averagePoints?.toFixed(1) || 0}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Games Played
                </Typography>
                <Typography variant="h6">{player.gamesPlayed || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Selected By
                </Typography>
                <Typography variant="h6">
                  {player.percentSelected?.toFixed(1) || 0}%
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Stats
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Goals</TableCell>
                      <TableCell align="right">{player.goals || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Assists</TableCell>
                      <TableCell align="right">{player.assists || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Clean Sheets</TableCell>
                      <TableCell align="right">
                        {player.cleanSheet || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Yellow Cards</TableCell>
                      <TableCell align="right">
                        {player.yellowCards || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Red Cards</TableCell>
                      <TableCell align="right">
                        {player.redCards || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Best Performance</TableCell>
                      <TableCell align="right">
                        {player.bestPerformance || 0} pts
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Info
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Player ID</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {player.id}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Match Name</TableCell>
                      <TableCell align="right">
                        {player.matchName || "N/A"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Current Points</TableCell>
                      <TableCell align="right">
                        {player.matchdayPoints || player.gameweekPoints || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Last 3 Avg</TableCell>
                      <TableCell align="right">
                        {player.last3Average?.toFixed(1) || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Bonus Points</TableCell>
                      <TableCell align="right">
                        {player.bonusPoints || 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Shirt Key</TableCell>
                      <TableCell align="right">
                        {player.shirtKey || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Nationality</TableCell>
                      <TableCell align="right">
                        {player.nationality || "-"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <EditPlayerModal
        open={editModalOpen}
        player={player}
        onClose={handleModalClose}
        onSave={handlePlayerUpdated}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PlayerDetailPage;
